import React, { useMemo, useRef } from "react";
import MessageList from "./MessageList";
import ChatInput from "./ChatInput";
import ChatHeader from "./ChatHeader";
import OnboardingForm from "./OnboardingForm";
import { useGoogleFont } from "../hooks/useGoogleFont.js";
import { useAutoScroll } from "../hooks/useAutoScroll.js";

const DEFAULT_AGENT_BUBBLE_COLOR = "#ffffff";
const DEFAULT_USER_MESSAGE_COLOR = "#f5f5f5";
const DEFAULT_TEXT_COLOR = "#000000";
const DARK_AGENT_BUBBLE_COLOR = "#1f2937";
const DARK_TEXT_COLOR = "#f3f4f6";
const DARK_USER_MESSAGE_BRAND_FALLBACK = "#2563eb";

const normalizeHex = (value) => value?.trim().toLowerCase();

const isDefaultColor = (value, defaultHex) => {
  if (!value) return true;
  return normalizeHex(value) === defaultHex;
};

const formatTime = (date) =>
  date instanceof Date
    ? date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    })
    : new Date(date).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });

const ChatWindow = ({
  isOpen,
  isMinimized,
  isDark,
  primaryColor,
  textColor,
  conciergeName,
  companyName,
  companyLogo,
  messages,
  isLoading,
  inputValue,
  setInputValue,
  onSend,
  onDirectSend,
  onToggleTheme,
  onMinimize,
  onClose,
  quickQuestions = [],
  quickQuestionsEnabled,
  onQuickQuestion,
  quickQuestionsLayout = "vertical",
  agentMessageBubbleColor,
  userMessageBoxColor,
  assistantTextColor,
  userTextColor,
  fontFamily = "Inter",
  agentMessageBubbleFontFamily,
  agentMessageBubbleFontSize,
  userMessageBubbleFontFamily,
  userMessageBubbleFontSize,
  fontSize = "14px",
  defaultLanguage = "en",
  onDecision,
  headerTextBold = false,
  headerTextItalic = false,
  enableVoiceInteraction = false,
  isVoiceSessionActive = false,
  voiceStatus = "idle",
  voiceError = null,
  onVoiceToggle,
  onBidiSubmit,
  disclaimerText,
  disclaimerPosition = "top",
  showPoweredBy = true,
  apiBaseUrl,
  apiKey,
  organizationId,
  onboardingQuestions = [],
  onboardingActive = false,
  onboardingCompleted = false,
  onOnboardingFormSubmit,
  collectionPrompt,
}) => {
  useGoogleFont(fontFamily);
  const messagesContainerRef = useRef(null);

  // Auto-scroll hook
  useAutoScroll(
    messagesContainerRef,
    [messages, isLoading],
    {
      enabled: true,
      threshold: 30,
      behavior: "smooth",
      delay: 0,
    }
  );

  const defaultLanguageCode = defaultLanguage?.toLowerCase?.();
  const isRtlLanguage = Boolean(defaultLanguageCode && defaultLanguageCode.startsWith("ar"));

  const resolvedAgentBubbleColor = useMemo(() => {
    if (isDark) {
      if (isDefaultColor(agentMessageBubbleColor, DEFAULT_AGENT_BUBBLE_COLOR)) {
        return DARK_AGENT_BUBBLE_COLOR;
      }
      return agentMessageBubbleColor || DARK_AGENT_BUBBLE_COLOR;
    }
    return agentMessageBubbleColor || DEFAULT_AGENT_BUBBLE_COLOR;
  }, [agentMessageBubbleColor, isDark]);

  const resolvedTextColor = useMemo(() => {
    if (isDark) {
      if (isDefaultColor(textColor, DEFAULT_TEXT_COLOR)) {
        return DARK_TEXT_COLOR;
      }
      return textColor || DARK_TEXT_COLOR;
    }
    return textColor || DEFAULT_TEXT_COLOR;
  }, [isDark, textColor]);

  const resolvedUserMessageColor = useMemo(() => {
    if (isDark) {
      if (isDefaultColor(userMessageBoxColor, DEFAULT_USER_MESSAGE_COLOR)) {
        return primaryColor || DARK_USER_MESSAGE_BRAND_FALLBACK;
      }
      return (
        userMessageBoxColor || primaryColor || DARK_USER_MESSAGE_BRAND_FALLBACK
      );
    }
    return userMessageBoxColor || DEFAULT_USER_MESSAGE_COLOR;
  }, [isDark, primaryColor, userMessageBoxColor]);

  const placeholder = isRtlLanguage ? "اكتب رسالتك هنا..." : "Try Your Own Message...";

  const hasUserMessages = messages?.some?.((msg) => msg.role === "user");
  const shouldShowQuickQuestions = !hasUserMessages && quickQuestions.length > 0 && quickQuestionsEnabled;

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className={`${isMinimized ? "w-80" : "w-[380px]"} max-w-[92vw] flex-shrink-0 border rounded-2xl shadow-2xl bg-white border-gray-200 flex flex-col ${isMinimized ? "h-auto" : "h-[650px] max-h-[85vh]"
        } overflow-hidden z-50 ${isDark ? "bg-gray-900 border-gray-700" : ""
        }`}
    >
      <ChatHeader
        conciergeName={conciergeName}
        companyName={companyName}
        companyLogo={companyLogo}
        primaryColor={primaryColor}
        isDark={isDark}
        isMinimized={isMinimized}
        textColor={resolvedTextColor}
        fontFamily={fontFamily}
        fontSize={fontSize}
        onToggleTheme={onToggleTheme}
        onMinimize={onMinimize}
        onClose={onClose}
        headerTextBold={headerTextBold}
        headerTextItalic={headerTextItalic}
      />

      {!isMinimized && (
        <>
          <div
            ref={messagesContainerRef}
            className={`chat-messages flex-1 overflow-y-auto px-5 py-4 space-y-4 ${isDark
              ? "bg-gray-900"
              : "bg-gradient-to-b from-gray-50/50 to-white"
              } break-words`}
          >
            {onboardingActive && !onboardingCompleted && onOnboardingFormSubmit ? (
              <OnboardingForm
                questions={onboardingQuestions}
                collapsiblePrompt={collectionPrompt}
                onSubmit={onOnboardingFormSubmit}
                primaryColor={primaryColor}
                isRtl={isRtlLanguage}
                isDark={isDark}
              />
            ) : (
            <MessageList
              messages={messages}
              isDark={isDark}
              primaryColor={primaryColor}
              isLoading={isLoading}
              agentMessageBubbleColor={agentMessageBubbleColor}
              userMessageBoxColor={userMessageBoxColor}
              textColor={textColor}
              assistantTextColor={assistantTextColor}
              userTextColor={userTextColor}
              fontFamily={fontFamily}
              agentMessageBubbleFontFamily={agentMessageBubbleFontFamily}
              agentMessageBubbleFontSize={agentMessageBubbleFontSize}
              userMessageBubbleFontFamily={userMessageBubbleFontFamily}
              userMessageBubbleFontSize={userMessageBubbleFontSize}
              fontSize={fontSize}
              formatTime={formatTime}
              isRtl={isRtlLanguage}
              quickQuestions={quickQuestions}
              shouldShowQuickQuestions={shouldShowQuickQuestions}
              onQuickQuestion={onQuickQuestion ?? onDirectSend}
              quickQuestionsLayout={quickQuestionsLayout}
              onDecision={onDecision}
              disclaimerText={disclaimerText}
              disclaimerPosition={disclaimerPosition}
              resolvedAgentBubbleColor={resolvedAgentBubbleColor}
              resolvedUserMessageColor={resolvedUserMessageColor}
              resolvedTextColor={resolvedTextColor}
              isVoiceSessionActive={isVoiceSessionActive}
              voiceStatus={voiceStatus}
              messagesContainerRef={messagesContainerRef}
              companyLogo={companyLogo}
              conciergeName={conciergeName}
              companyName={companyName}
            />
            )}
          </div>

          {!(onboardingActive && !onboardingCompleted) && (
          <ChatInput
            inputValue={inputValue}
            setInputValue={setInputValue}
            onSend={onSend}
            isDark={isDark}
            primaryColor={primaryColor}
            isOpen={isOpen}
            isMinimized={isMinimized}
            isLoading={isLoading}
            placeholder={placeholder}
            textColor={resolvedTextColor}
            fontFamily={fontFamily}
            userMessageBubbleFontFamily={userMessageBubbleFontFamily}
            userMessageBubbleFontSize={userMessageBubbleFontSize}
            fontSize={fontSize}
            isRtl={isRtlLanguage}
            enableVoiceInteraction={enableVoiceInteraction}
            isVoiceSessionActive={isVoiceSessionActive}
            voiceStatus={voiceStatus}
            voiceError={voiceError}
            onVoiceToggle={onVoiceToggle}
            onBidiSubmit={onBidiSubmit}
            disclaimerText={disclaimerText}
            disclaimerPosition={disclaimerPosition}
            showPoweredBy={showPoweredBy}
            onboardingQuestions={onboardingQuestions}
            onboardingActive={onboardingActive}
            onboardingCompleted={onboardingCompleted}
          />
          )}
        </>
      )}
    </div>
  );
};

export default ChatWindow;
